<template>
  <div class="leaderboards-view min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-purple-50">
    <!-- Page Header -->
    <PageHeader />

    <!-- Main Content -->
    <div class="leaderboards-container px-4 py-8 max-w-7xl mx-auto">
      <!-- Page Title Section -->
      <div class="page-title-section text-center mb-8">
        <div class="title-decoration mb-4">
          <i class="fas fa-trophy text-6xl text-yellow-500 animate-bounce"></i>
        </div>
        <h1 class="text-4xl md:text-5xl font-bold text-gray-800 mb-3">
          Leaderboards
        </h1>
        <p class="text-lg text-gray-600">
          See how you rank among the world's greatest explorers!
        </p>
      </div>

      <!-- User Position Card (if authenticated) -->
      <UserPositionCard
        v-if="isAuthenticated && userStats"
        :user-stats="userStats"
      />

      <!-- Tabs Section -->
      <div class="tabs-section mb-8">
        <div class="tabs-wrapper">
          <button
            @click="activeTab = 'global'"
            class="tab-button"
            :class="{ 'active': activeTab === 'global' }"
          >
            <i class="fas fa-globe"></i>
            <span>Global</span>
          </button>
          <button
            @click="activeTab = 'country'"
            class="tab-button"
            :class="{ 'active': activeTab === 'country' }"
          >
            <i class="fas fa-flag"></i>
            <span>Country</span>
          </button>
        </div>

        <!-- Country Selector (only visible when Country tab is active) -->
        <transition name="fade">
          <div v-if="activeTab === 'country'" class="country-selector-wrapper mt-6">
            <CountrySelector
              v-model="selectedCountry"
              @change="onCountryChange"
            />
          </div>
        </transition>
      </div>

      <!-- Refresh Button -->
      <div class="actions-bar mb-6 flex justify-between items-center">
        <div class="results-info text-gray-600">
          <span v-if="activeTab === 'global' && hasGlobalData">
            Showing top {{ globalLeaderboard.length }} explorers
          </span>
          <span v-else-if="activeTab === 'country' && hasCountryData">
            Showing top {{ countryLeaderboard.length }} explorers for {{ currentCountryCode }}
          </span>
        </div>
        <button
          @click="refreshLeaderboard"
          :disabled="isLoading"
          class="refresh-button"
        >
          <i class="fas fa-sync-alt" :class="{ 'animate-spin': isLoading }"></i>
          <span>Refresh</span>
        </button>
      </div>

      <!-- Global Leaderboard -->
      <div v-if="activeTab === 'global'" class="leaderboard-content">
        <!-- Loading State -->
        <LoadingState
          v-if="globalLoading"
          message="Loading global leaderboard..."
        />

        <!-- Error State -->
        <div v-else-if="globalError" class="error-message">
          <i class="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
          <p class="text-lg text-gray-700 mb-2">{{ globalError }}</p>
          <button @click="loadGlobalLeaderboard" class="retry-button">
            <i class="fas fa-redo"></i>
            Try Again
          </button>
        </div>

        <!-- Empty State -->
        <EmptyState
          v-else-if="!hasGlobalData"
          title="No Rankings Yet"
          message="Be the first to take a quiz and claim the top spot on the global leaderboard!"
          :show-action="true"
          action-text="Take a Quiz"
          @action="goToQuiz"
        />

        <!-- Leaderboard Data -->
        <div v-else>
          <!-- Desktop: Table View -->
          <div class="desktop-view">
            <LeaderboardTable
              :entries="globalLeaderboard"
              :current-user-id="currentUserId"
            />
          </div>

          <!-- Mobile: Card View -->
          <div class="mobile-view">
            <div class="cards-grid">
              <LeaderboardCard
                v-for="entry in globalLeaderboard"
                :key="entry.user_id"
                :entry="entry"
                :current-user-id="currentUserId"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Country Leaderboard -->
      <div v-else-if="activeTab === 'country'" class="leaderboard-content">
        <!-- Country Not Selected -->
        <div v-if="!selectedCountry" class="empty-selection">
          <i class="fas fa-globe-americas text-6xl text-gray-300 mb-4"></i>
          <h3 class="text-2xl font-bold text-gray-700 mb-2">Select a Country</h3>
          <p class="text-gray-500">Choose a country from the dropdown above to view its leaderboard</p>
        </div>

        <!-- Loading State -->
        <LoadingState
          v-else-if="countryLoading"
          :message="`Loading ${selectedCountry} leaderboard...`"
        />

        <!-- Error State -->
        <div v-else-if="countryError" class="error-message">
          <i class="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
          <p class="text-lg text-gray-700 mb-2">{{ countryError }}</p>
          <button @click="loadCountryLeaderboard" class="retry-button">
            <i class="fas fa-redo"></i>
            Try Again
          </button>
        </div>

        <!-- Empty State -->
        <EmptyState
          v-else-if="!hasCountryData"
          title="No Rankings for This Country"
          :message="`No one has taken quizzes for ${selectedCountry} yet. Be the first!`"
          :show-action="true"
          action-text="Take a Quiz"
          @action="goToQuiz"
        />

        <!-- Leaderboard Data -->
        <div v-else>
          <!-- Desktop: Table View -->
          <div class="desktop-view">
            <LeaderboardTable
              :entries="countryLeaderboard"
              :current-user-id="currentUserId"
              :show-country="true"
            />
          </div>

          <!-- Mobile: Card View -->
          <div class="mobile-view">
            <div class="cards-grid">
              <LeaderboardCard
                v-for="entry in countryLeaderboard"
                :key="entry.user_id"
                :entry="entry"
                :current-user-id="currentUserId"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Captain Leo Mascot Section -->
      <div class="mascot-section text-center mt-12 mb-8">
        <div class="mascot-message bg-white rounded-2xl shadow-lg p-6 max-w-2xl mx-auto">
          <div class="mascot-icon mb-4">
            <img
              v-if="logoPath"
              :src="logoPath"
              alt="Captain Leo"
              class="w-24 h-24 mx-auto rounded-full"
            />
            <i v-else class="fas fa-anchor text-6xl text-purple-600"></i>
          </div>
          <p class="text-lg text-gray-700 italic">
            "Keep exploring and learning! Every quiz makes you a better explorer!"
          </p>
          <p class="text-sm text-gray-500 mt-2">- Captain Leo</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PageHeader from '@/components/PageHeader.vue'
import UserPositionCard from '@/components/leaderboard/UserPositionCard.vue'
import CountrySelector from '@/components/leaderboard/CountrySelector.vue'
import LeaderboardTable from '@/components/leaderboard/LeaderboardTable.vue'
import LeaderboardCard from '@/components/leaderboard/LeaderboardCard.vue'
import LoadingState from '@/components/leaderboard/LoadingState.vue'
import EmptyState from '@/components/leaderboard/EmptyState.vue'

export default {
  name: 'LeaderboardsView',
  components: {
    PageHeader,
    UserPositionCard,
    CountrySelector,
    LeaderboardTable,
    LeaderboardCard,
    LoadingState,
    EmptyState
  },
  data() {
    return {
      activeTab: 'global', // 'global' or 'country'
      selectedCountry: null
    }
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'user']),
    ...mapGetters('leaderboard', [
      'globalLeaderboard',
      'globalLoading',
      'globalError',
      'hasGlobalData',
      'countryLeaderboard',
      'currentCountryCode',
      'countryLoading',
      'countryError',
      'hasCountryData',
      'userStats',
      'userStatsLoading'
    ]),
    isLoading() {
      return this.activeTab === 'global' ? this.globalLoading : this.countryLoading
    },
    currentUserId() {
      return this.userStats?.user_id || null
    },
    logoPath() {
      try {
        return require('@/assets/logo.png')
      } catch {
        return null
      }
    }
  },
  async mounted() {
    // Load initial data
    await this.loadGlobalLeaderboard()

    // Load user stats if authenticated
    if (this.isAuthenticated) {
      await this.loadUserStats()
    }
  },
  methods: {
    ...mapActions('leaderboard', [
      'fetchGlobalLeaderboard',
      'fetchCountryLeaderboard',
      'fetchUserStats'
    ]),

    async loadGlobalLeaderboard() {
      await this.fetchGlobalLeaderboard({ limit: 100 })
    },

    async loadCountryLeaderboard() {
      if (!this.selectedCountry) return
      await this.fetchCountryLeaderboard({
        countryCode: this.selectedCountry,
        limit: 50
      })
    },

    async loadUserStats() {
      await this.fetchUserStats()
    },

    async onCountryChange(countryCode) {
      this.selectedCountry = countryCode
      await this.loadCountryLeaderboard()
    },

    async refreshLeaderboard() {
      if (this.activeTab === 'global') {
        await this.fetchGlobalLeaderboard({ limit: 100, forceRefresh: true })
      } else if (this.selectedCountry) {
        await this.fetchCountryLeaderboard({
          countryCode: this.selectedCountry,
          limit: 50,
          forceRefresh: true
        })
      }

      // Refresh user stats if authenticated
      if (this.isAuthenticated) {
        await this.fetchUserStats({ forceRefresh: true })
      }
    },

    goToQuiz() {
      // Navigate to quiz - you can customize this based on your quiz routes
      this.$router.push('/app')
    }
  }
}
</script>

<style scoped>
.leaderboards-view {
  min-height: 100vh;
}

.leaderboards-container {
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Page Title */
.page-title-section {
  animation: slideDown 0.6s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Tabs */
.tabs-section {
  animation: fadeIn 0.7s ease-out;
}

.tabs-wrapper {
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 32px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.tab-button:hover {
  border-color: #667eea;
  color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.tab-button.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: transparent;
  color: white;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
}

.tab-button i {
  font-size: 1.25rem;
}

.country-selector-wrapper {
  display: flex;
  justify-content: center;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Actions Bar */
.actions-bar {
  animation: fadeIn 0.8s ease-out;
}

.results-info {
  font-size: 0.875rem;
  font-weight: 500;
}

.refresh-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: white;
  border: 2px solid #667eea;
  border-radius: 12px;
  color: #667eea;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.refresh-button:hover:not(:disabled) {
  background: #667eea;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.refresh-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Leaderboard Content */
.leaderboard-content {
  animation: fadeIn 0.9s ease-out;
}

/* Desktop/Mobile Views */
.desktop-view {
  display: block;
}

.mobile-view {
  display: none;
}

@media (max-width: 768px) {
  .desktop-view {
    display: none;
  }

  .mobile-view {
    display: block;
  }
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

/* Empty Selection */
.empty-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}

/* Error Message */
.error-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}

.retry-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 16px;
}

.retry-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
}

/* Mascot Section */
.mascot-section {
  animation: fadeIn 1s ease-out;
}

.mascot-icon {
  animation: bounce 2s ease-in-out infinite;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .page-title-section h1 {
    font-size: 2rem;
  }

  .page-title-section p {
    font-size: 1rem;
  }

  .tabs-wrapper {
    gap: 12px;
  }

  .tab-button {
    padding: 12px 24px;
    font-size: 0.9375rem;
  }

  .cards-grid {
    grid-template-columns: 1fr;
  }

  .actions-bar {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .results-info {
    text-align: center;
  }

  .refresh-button {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .leaderboards-container {
    padding-left: 12px;
    padding-right: 12px;
  }

  .tab-button {
    padding: 10px 20px;
    font-size: 0.875rem;
  }

  .tab-button span {
    display: none;
  }

  .tab-button i {
    font-size: 1.5rem;
  }
}
</style>
